// Shared report-PDF preview frame used by BOTH report drawers
// (LoanReportPreviewDrawer — Insurance/UCC/QA — and the Financial
// Reporting / DW-M1 SeriesReportPreviewDrawer). The server renders the PDF
// on demand (HTML → LibreOffice) at the preview endpoint, which can take a
// minute on a cold report — a bare iframe src gives no load/error signal
// for PDFs, so the frame fetches the bytes itself: a centered progress
// message while rendering, an inline error with retry on failure, and an
// onReadyChange signal the drawers use to keep Submit/Certify/File disabled
// until a preview actually exists (nobody certifies a report they never
// saw).

import { useCallback, useEffect, useState } from "react";
import { Button } from "@/design-system/components/ui";

interface ReportPdfFrameProps {
  /** Raw-PDF preview URL (loanReportsApi.previewUrl). */
  src: string;
  /** Fires with true once the PDF is displayable, false while it is not. */
  onReadyChange?: (ready: boolean) => void;
  testId?: string;
}

type FrameState =
  | { kind: "loading" }
  | { kind: "ready"; objectUrl: string }
  | { kind: "error" };

export function ReportPdfFrame({ src, onReadyChange, testId }: ReportPdfFrameProps) {
  const [state, setState] = useState<FrameState>({ kind: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setState({ kind: "loading" });
    onReadyChange?.(false);

    fetch(src, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Preview failed (${res.status})`);
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setState({ kind: "ready", objectUrl });
        onReadyChange?.(true);
      })
      .catch(() => {
        if (cancelled) return;
        setState({ kind: "error" });
        onReadyChange?.(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // onReadyChange is deliberately omitted: parents pass inline setters and
    // re-fetching the PDF on every parent render would defeat the point.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  if (state.kind === "loading") {
    return (
      <div
        className="flex min-h-[60vh] w-full flex-1 flex-col items-center justify-center gap-space-3 rounded-md border border-border-light bg-white-pure"
        data-testid={testId ? `${testId}-loading` : "report-pdf-frame-loading"}
        role="status"
      >
        <span
          className="h-8 w-8 animate-spin rounded-pill border-2 border-gold-shadow border-t-transparent"
          aria-hidden
        />
        <p className="m-0 text-small text-gray-mid">
          Generating report — this can take a minute or two.
        </p>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div
        className="flex min-h-[60vh] w-full flex-1 flex-col items-center justify-center gap-space-3 rounded-md border border-border-light bg-white-pure"
        data-testid={testId ? `${testId}-error` : "report-pdf-frame-error"}
        role="alert"
      >
        <p className="m-0 text-small text-status-error">
          The report preview could not be generated.
        </p>
        <Button variant="secondary" size="sm" onClick={retry}>
          Retry
        </Button>
      </div>
    );
  }

  return (
    <iframe
      title="Report preview"
      src={state.objectUrl}
      className="min-h-[60vh] w-full flex-1 rounded-md border border-border-light bg-white-pure"
      data-testid={testId ?? "report-pdf-frame"}
    />
  );
}
